import React, { useState, useEffect } from 'react';
import { FaVideo, FaPlus, FaCalendarAlt, FaClock, FaUsers, FaExternalLinkAlt, FaTimes, FaSpinner, FaTrash, FaLink, FaCheckCircle, FaExclamationCircle, FaUpload, FaFileVideo } from 'react-icons/fa';

const MeetingManagement = () => {
  const [meetings, setMeetings] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [activeTab, setActiveTab] = useState('upcoming');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [uploadingRecording, setUploadingRecording] = useState(false);
  const [recordingFile, setRecordingFile] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startTime: '',
    participantIds: []
  });

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    checkZohoAuth();
    fetchMeetings();
    fetchUsers();

    // Check for OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    const oauthStatus = urlParams.get('oauth');
    if (oauthStatus === 'success') {
      alert('Zoho Meeting connected successfully!');
      setIsAuthenticated(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (oauthStatus === 'error') {
      alert('Failed to connect Zoho Meeting. Please try again.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const checkZohoAuth = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/meetings/oauth/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setIsAuthenticated(data.isAuthenticated);
      }
    } catch (error) {
      console.error('Error checking Zoho auth:', error);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleConnectZoho = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/meetings/oauth/url`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.authUrl) {
        window.location.href = data.authUrl;
      } else {
        alert('Failed to get authorization URL');
      }
    } catch (error) {
      console.error('Error getting OAuth URL:', error);
      alert('Failed to connect to Zoho');
    }
  };

  const fetchMeetings = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/meetings/admin/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setMeetings(data.meetings);
      }
    } catch (error) {
      console.error('Error fetching meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/meetings/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setUsers(data.users);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const token = localStorage.getItem('adminToken');
      const selectedUsers = users.filter(u => formData.participantIds.includes(u._id));
      
      const payload = {
        title: formData.title,
        description: formData.description,
        startTime: new Date(formData.startTime).toISOString(),
        duration: 30,
        participants: selectedUsers.map(user => ({
          participantId: user._id,
          participantType: 'User',
          name: user.name || user.businessName,
          email: user.email
        }))
      };

      const response = await fetch(`${API_BASE_URL}/api/meetings/admin/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        alert('Meeting created successfully! Notifications sent to participants.');
        setShowCreateModal(false);
        setFormData({ title: '', description: '', startTime: '', participantIds: [] });
        fetchMeetings();
      } else {
        alert(data.message || 'Failed to create meeting');
      }
    } catch (error) {
      console.error('Error creating meeting:', error);
      alert('Failed to create meeting. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to delete this meeting?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/meetings/${meetingId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        alert('Meeting deleted successfully');
        fetchMeetings();
      } else {
        alert(data.message || 'Failed to delete meeting');
      }
    } catch (error) {
      console.error('Error deleting meeting:', error);
      alert('Failed to delete meeting');
    }
  };

  const handleParticipantChange = (userId) => {
    setFormData(prev => {
      const newIds = prev.participantIds.includes(userId)
        ? prev.participantIds.filter(id => id !== userId)
        : [...prev.participantIds, userId];
      return { ...prev, participantIds: newIds };
    });
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'Asia/Kolkata' }),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }) + ' IST'
    };
  };

  const getMeetingStatus = (meeting) => {
    const now = new Date();
    const startTime = new Date(meeting.startTime);
    const endTime = new Date(startTime.getTime() + meeting.duration * 60000);

    if (meeting.status === 'cancelled') return 'cancelled';
    if (now >= startTime && now <= endTime) return 'ongoing';
    if (now > endTime) return 'completed';
    return 'upcoming';
  };

  const filterMeetings = () => {
    return meetings.filter(meeting => {
      const status = getMeetingStatus(meeting);
      if (activeTab === 'upcoming') return status === 'upcoming';
      if (activeTab === 'ongoing') return status === 'ongoing';
      if (activeTab === 'past') return status === 'completed' || status === 'cancelled';
      return true;
    });
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() + 15);
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const mins = String(now.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${mins}`;
  };

  const copyJoinLink = (url) => {
    navigator.clipboard.writeText(url);
    alert('Join link copied to clipboard!');
  };

  const handleTriggerRecordingCheck = async () => {
    if (!window.confirm('This will check all completed meetings for available recordings and automatically upload them to user documents. Continue?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE_URL}/api/meetings/admin/trigger-recording-check`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        alert('Recording check triggered successfully! Check the server console for details.');
        fetchMeetings();
      } else {
        alert(data.message || 'Failed to trigger recording check');
      }
    } catch (error) {
      console.error('Error triggering recording check:', error);
      alert('Failed to trigger recording check');
    }
  };

  const handleUploadRecording = (meeting) => {
    setSelectedMeeting(meeting);
    setShowUploadModal(true);
    setRecordingFile(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type (video formats)
      const validTypes = ['video/mp4', 'video/webm', 'video/avi', 'video/mov', 'video/quicktime'];
      if (!validTypes.includes(file.type)) {
        alert('Please upload a valid video file (MP4, WebM, AVI, MOV)');
        return;
      }
      // Check file size (max 500MB)
      if (file.size > 500 * 1024 * 1024) {
        alert('File size must be less than 500MB');
        return;
      }
      setRecordingFile(file);
    }
  };

  const uploadRecordingToServer = async () => {
    if (!recordingFile || !selectedMeeting) return;

    setUploadingRecording(true);
    try {
      const token = localStorage.getItem('adminToken');
      
      // Upload file to server
      const formData = new FormData();
      formData.append('file', recordingFile);

      const uploadResponse = await fetch(`${API_BASE_URL}/api/admin/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      const uploadData = await uploadResponse.json();
      if (!uploadData.success) {
        throw new Error(uploadData.message || 'Failed to upload file');
      }

      // Get the user ID from the meeting participants
      const userId = selectedMeeting.participants && selectedMeeting.participants.length > 0 
        ? selectedMeeting.participants[0].participantId 
        : null;

      if (!userId) {
        alert('No user found for this meeting. Cannot upload recording.');
        setUploadingRecording(false);
        return;
      }

      // Add recording to user's documents
      const recordingResponse = await fetch(
        `${API_BASE_URL}/api/meetings/${selectedMeeting._id}/upload-recording`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            userId,
            fileUrl: uploadData.fileUrl,
            originalName: recordingFile.name,
            size: recordingFile.size
          })
        }
      );

      const recordingData = await recordingResponse.json();
      if (recordingData.success) {
        alert('Meeting recording uploaded successfully to user documents! It will be automatically deleted after 3 months.');
        setShowUploadModal(false);
        setRecordingFile(null);
        setSelectedMeeting(null);
        fetchMeetings();
      } else {
        alert(recordingData.message || 'Failed to add recording to user documents');
      }
    } catch (error) {
      console.error('Error uploading recording:', error);
      alert('Failed to upload recording. Please try again.');
    } finally {
      setUploadingRecording(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px]">
          <FaSpinner className="w-8 h-8 text-primary-600 animate-spin mb-4" />
          <p className="text-gray-600 dark:text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-xl">
            <FaVideo className="w-6 h-6 text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Meeting Management</h1>
        </div>
        <div className="flex items-center gap-3">
          {!isAuthenticated ? (
            <button onClick={handleConnectZoho} className="flex items-center gap-2 px-4 py-2 border border-primary-500 text-primary-600 font-medium rounded-xl hover:bg-primary-50 dark:hover:bg-primary-900/20">
              <FaLink /> Connect Zoho Meeting
            </button>
          ) : (
            <>
              <div className="flex items-center gap-2 px-4 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-xl font-medium">
                <FaCheckCircle /> Zoho Connected
              </div>
              <button 
                onClick={handleTriggerRecordingCheck}
                className="flex items-center gap-2 px-4 py-2 border border-purple-500 text-purple-600 font-medium rounded-xl hover:bg-purple-50 dark:hover:bg-purple-900/20"
                title="Check for recordings and auto-upload"
              >
                <FaUpload /> Check Recordings
              </button>
            </>
          )}
          <button 
            onClick={() => setShowCreateModal(true)}
            disabled={!isAuthenticated}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaPlus /> Create Meeting
          </button>
        </div>
      </div>

      {/* Auth Warning */}
      {!isAuthenticated && (
        <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-xl mb-6">
          <FaExclamationCircle className="w-5 h-5 flex-shrink-0" />
          <span>Please connect your Zoho Meeting account to create and manage meetings.</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm">
          <div className="text-3xl font-bold text-primary-600 mb-1">{meetings.filter(m => getMeetingStatus(m) === 'upcoming').length}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Upcoming</div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm">
          <div className="text-3xl font-bold text-green-600 mb-1">{meetings.filter(m => getMeetingStatus(m) === 'ongoing').length}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Ongoing</div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm">
          <div className="text-3xl font-bold text-gray-600 dark:text-gray-400 mb-1">{meetings.filter(m => getMeetingStatus(m) === 'completed').length}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Completed</div>
        </div>
        <div className="bg-white dark:bg-dark-800 rounded-2xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm">
          <div className="text-3xl font-bold text-purple-600 mb-1">{meetings.length}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">Total</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 dark:bg-dark-700 p-1 rounded-xl w-fit">
        {['upcoming', 'ongoing', 'past', 'all'].map(tab => (
          <button 
            key={tab}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === tab ? 'bg-white dark:bg-dark-800 text-primary-600 shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-dark-800 rounded-2xl border border-gray-100 dark:border-dark-700 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-dark-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Meeting</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Date & Time</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Duration</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Created By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filterMeetings().length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <FaCalendarAlt className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">No {activeTab === 'all' ? '' : activeTab} meetings found</p>
                  </td>
                </tr>
              ) : (
                filterMeetings().map(meeting => {
                  const { date, time } = formatDateTime(meeting.startTime);
                  const status = getMeetingStatus(meeting);
                  return (
                    <tr key={meeting._id} className="border-b border-gray-100 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-700/50">
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="font-semibold text-gray-900 dark:text-white">{meeting.title}</span>
                          {meeting.description && <span className="text-sm text-gray-500 dark:text-gray-400 mt-1">{meeting.description.substring(0, 50)}...</span>}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="text-gray-900 dark:text-white">{date}</span>
                          <span className="text-sm text-gray-500 dark:text-gray-400">{time}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-gray-700 dark:text-gray-300">{meeting.duration} mins</td>
                      <td className="px-4 py-4">
                        <div className="flex flex-col">
                          <span className="text-gray-900 dark:text-white">{meeting.creatorName}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{meeting.creatorType}</span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${meeting.meetingType === 'admin-to-user' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'}`}>
                          {meeting.meetingType === 'admin-to-user' ? 'Admin → User' : 'User → Admin'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
                          status === 'upcoming' ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400' :
                          status === 'ongoing' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                          status === 'completed' ? 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' :
                          'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                        }`}>
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          {(status === 'upcoming' || status === 'ongoing') && meeting.hostUrl && (
                            <a 
                              href={meeting.hostUrl} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg"
                              title="Start Meeting"
                            >
                              <FaVideo />
                            </a>
                          )}
                          {meeting.joinUrl && (
                            <button 
                              onClick={() => copyJoinLink(meeting.joinUrl)}
                              className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg"
                              title="Copy Join Link"
                            >
                              <FaLink />
                            </button>
                          )}
                          {status === 'completed' && meeting.recordingStatus !== 'available' && (
                            <button 
                              onClick={() => handleUploadRecording(meeting)}
                              className="p-2 text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg"
                              title="Upload Recording"
                            >
                              <FaUpload />
                            </button>
                          )}
                          {meeting.recordingStatus === 'available' && (
                            <div className="p-2 text-green-600" title="Recording Uploaded">
                              <FaFileVideo />
                            </div>
                          )}
                          <button 
                            onClick={() => handleDeleteMeeting(meeting._id)}
                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                            title="Delete Meeting"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload Recording Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowUploadModal(false)}>
          <div className="bg-white dark:bg-dark-800 rounded-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-dark-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Upload Meeting Recording</h2>
              <button onClick={() => setShowUploadModal(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg">
                <FaTimes />
              </button>
            </div>
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                  <strong>Meeting:</strong> {selectedMeeting?.title}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Recording will be added to the user's Documents section and automatically deleted after 3 months.
                </p>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Select Video File *</label>
                <input
                  type="file"
                  accept="video/*"
                  onChange={handleFileChange}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
                {recordingFile && (
                  <div className="mt-3 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg">
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      <strong>File:</strong> {recordingFile.name}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <strong>Size:</strong> {(recordingFile.size / (1024 * 1024)).toFixed(2)} MB
                    </p>
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Max file size: 500MB. Supported formats: MP4, WebM, AVI, MOV</p>
              </div>
              <div className="flex gap-3">
                <button 
                  type="button" 
                  onClick={() => setShowUploadModal(false)} 
                  className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-dark-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-dark-700"
                >
                  Cancel
                </button>
                <button 
                  onClick={uploadRecordingToServer}
                  disabled={!recordingFile || uploadingRecording}
                  className="flex-1 px-4 py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {uploadingRecording ? <><FaSpinner className="animate-spin" /> Uploading...</> : <><FaUpload /> Upload</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white dark:bg-dark-800 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-dark-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Create New Meeting</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg">
                <FaTimes />
              </button>
            </div>
            <form onSubmit={handleCreateMeeting} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Meeting Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter meeting title"
                  required
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter meeting description (optional)"
                  rows={3}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Start Date & Time *</label>
                <input
                  type="datetime-local"
                  value={formData.startTime}
                  onChange={e => setFormData({ ...formData, startTime: e.target.value })}
                  min={getMinDateTime()}
                  required
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Duration: 30 minutes (fixed). As admin, you can schedule meetings at any time.</p>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Invite Users</label>
                <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-dark-600 rounded-xl p-3 bg-gray-50 dark:bg-dark-700">
                  {users.length === 0 ? (
                    <p className="text-gray-500 dark:text-gray-400 text-sm text-center py-4">No users available</p>
                  ) : (
                    users.map(user => (
                      <label key={user._id} className="flex items-center gap-3 p-2 hover:bg-white dark:hover:bg-dark-600 rounded-lg cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.participantIds.includes(user._id)}
                          onChange={() => handleParticipantChange(user._id)}
                          className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                        <div className="flex flex-col">
                          <span className="font-medium text-gray-900 dark:text-white text-sm">{user.name || user.businessName || 'N/A'}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{user.email}</span>
                        </div>
                      </label>
                    ))
                  )}
                </div>
                {formData.participantIds.length > 0 && (
                  <p className="text-sm text-primary-600 mt-2">{formData.participantIds.length} user(s) selected</p>
                )}
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-dark-600 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-50 dark:hover:bg-dark-700">
                  Cancel
                </button>
                <button type="submit" disabled={creating} className="flex-1 px-4 py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                  {creating ? <><FaSpinner className="animate-spin" /> Creating...</> : 'Create Meeting'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default MeetingManagement;
